import { useCallback, useMemo } from "react";
import type { Control, FieldErrors } from "react-hook-form";
import type { ActiveCompanies } from "../../../../../../utils/backend-types";
import { FormFieldController } from "../../../../../BuildingBlocks/Forms/Controllers/FormFieldController";
import type { Contract } from "../../../../DeliveryOverview.types";
import type { ContractPersonOptions } from "../../ContractModal.types";
import { CONTRACT_FORM_FIELD_DATA } from "../ContractForm.constants";

interface SupplierAndSuppliedProps {
  activeCompanies?: ActiveCompanies;
  control: Control<Contract>;
  errors: FieldErrors<Contract>;
  selectedSite?: number;
  supplier: number | null;
  supplierChoices: ContractPersonOptions;
  supplied: number | null;
  suppliedChoices: ContractPersonOptions;
}

function SupplierAndSupplied({
  activeCompanies,
  control,
  errors,
  selectedSite,
  supplier,
  supplierChoices,
  supplied,
  suppliedChoices
}: SupplierAndSuppliedProps) {
  const filterChoices = useCallback(
    (choices: ContractPersonOptions, companyIdToRemove: number | null) => {
      let newChoices: ContractPersonOptions = [];

      if (activeCompanies && selectedSite) {
        const siteCompanies = activeCompanies.sites.find(
          (site) => site.id === selectedSite
        )?.activeCompanies;

        if (siteCompanies) {
          const siteCompanyChoices: ContractPersonOptions = siteCompanies.map(
            (company) => ({
              label: company.name,
              value: company.id
            })
          );

          if (!companyIdToRemove) {
            return siteCompanyChoices;
          } else {
            newChoices = siteCompanyChoices.filter(
              (choice) => choice.value !== companyIdToRemove
            );
          }
        }
      } else {
        if (!companyIdToRemove) {
          return choices;
        }

        newChoices =
          choices.filter((choice) => choice.value !== companyIdToRemove) || [];
      }

      return newChoices;
    },
    [activeCompanies, selectedSite]
  );

  const filteredSupplierChoices = useMemo(() => {
    return filterChoices(supplierChoices, supplied);
  }, [filterChoices, supplierChoices, supplied]);

  const filteredSuppliedChoices = useMemo(() => {
    return filterChoices(suppliedChoices, supplier);
  }, [filterChoices, suppliedChoices, supplier]);

  return (
    <div className="SupplierAndSupplied">
      <FormFieldController
        control={control}
        data={{
          ...CONTRACT_FORM_FIELD_DATA.supplier,
          data: filteredSupplierChoices,
          value: supplier ? supplier.toString() : null,
          onChange: (value) => {
            return value ? parseInt(value) : null;
          }
        }}
        error={errors.supplier}
      />
      <FormFieldController
        control={control}
        data={{
          ...CONTRACT_FORM_FIELD_DATA.supplied,
          data: filteredSuppliedChoices
        }}
        error={errors.supplied}
      />
    </div>
  );
}

export { SupplierAndSupplied };
